import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, ExternalLink, Loader2, MapPin, Mic, Search, Sparkles, Store, X } from "lucide-react";
import { formatErr } from "@/lib/api";
import { useVoiceRecorder } from "@/lib/useVoiceRecorder";

function StorePicker({ client, basePath, currentStore, onSelected, onCancel, T }) {
  const [zip, setZip] = useState("");
  const [stores, setStores] = useState([]);
  const [finding, setFinding] = useState(false);
  const [saving, setSaving] = useState(null);

  const find = async () => {
    if (!/^\d{5}$/.test(zip.trim())) {
      toast.error(T("Enter a 5-digit ZIP code."));
      return;
    }
    setFinding(true);
    try {
      const { data } = await client.post(`${basePath}/shopping-list/home-depot-stores`, { zip_code: zip.trim() });
      setStores(data.stores || []);
      if (!(data.stores || []).length) toast.error(T("No nearby Home Depot stores were found."));
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setFinding(false);
    }
  };

  const select = async (store) => {
    setSaving(store.store_id);
    try {
      const { data } = await client.put(`${basePath}/shopping-list/home-depot-store`, { store_id: store.store_id });
      onSelected(data.store);
      toast.success(T("Home Depot saved to your account."));
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="shopping-ai-store-picker">
      <div>
        <div className="text-sm font-semibold">{T("Which Home Depot do you use?")}</div>
        <p className="text-xs text-muted-foreground mt-1">
          {T("Enter your ZIP, choose a store once, and it will be remembered on your account.")}
        </p>
      </div>
      <div className="flex gap-2">
        <Input
          value={zip}
          onChange={(event) => setZip(event.target.value.replace(/\D/g, "").slice(0, 5))}
          onKeyDown={(event) => { if (event.key === "Enter") find(); }}
          inputMode="numeric"
          placeholder={T("ZIP code")}
          className="h-9 rounded-sm"
          data-testid="shopping-ai-store-zip"
        />
        <Button size="sm" className="h-9 rounded-sm" onClick={find} disabled={finding} data-testid="shopping-ai-store-find">
          {finding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="sr-only">{T("Find stores")}</span>
        </Button>
      </div>
      {stores.length > 0 && (
        <div className="space-y-2" data-testid="shopping-ai-store-results">
          {stores.map((store) => (
            <button
              type="button"
              key={store.store_id}
              onClick={() => select(store)}
              disabled={!!saving}
              className="w-full border border-border rounded-sm p-3 text-left hover:border-primary hover:bg-secondary/40 transition-colors"
              data-testid={`shopping-ai-store-option-${store.store_id}`}
            >
              <div className="flex items-start gap-2">
                <Store className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{store.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{store.address}</div>
                  <div className="text-xs font-medium mt-1">
                    {store.distance_mi == null ? store.postal_code : `${store.distance_mi} ${T("mi away")}`}
                  </div>
                </div>
                {saving === store.store_id && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
            </button>
          ))}
        </div>
      )}
      {currentStore && (
        <Button variant="ghost" size="sm" className="rounded-sm" onClick={onCancel} data-testid="shopping-ai-store-cancel-change">
          {T("Keep current store")}
        </Button>
      )}
    </div>
  );
}

// One selectable exact-product match (image, price, stock, confirm + open link).
function CandidateCard({ candidate, confirmed, confirming, onConfirm, storeName, T }) {
  return (
    <div className={`border rounded-sm p-2.5 ${confirmed ? "border-emerald-400 bg-emerald-50" : "border-border"}`} data-testid={`shopping-ai-product-${candidate.product_id}`}>
      <div className="flex gap-2.5">
        {candidate.image && (
          <img
            src={candidate.image}
            alt=""
            className="w-16 h-16 object-contain border border-border rounded-sm bg-white shrink-0"
            data-testid={`shopping-ai-product-image-${candidate.product_id}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold leading-snug">{candidate.title}</div>
          <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-2">
            {candidate.model_number && <span>Model {candidate.model_number}</span>}
            {candidate.price != null && <span>${Number(candidate.price).toFixed(2)}</span>}
          </div>
          <div className="text-xs font-semibold mt-1">
            {candidate.stock_quantity == null
              ? T("Store stock unavailable")
              : `${candidate.stock_quantity} ${T("in stock")} · ${candidate.stock_store || storeName}`}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <Button
          size="sm"
          className="rounded-sm h-9 flex-1"
          onClick={onConfirm}
          disabled={!!confirming || confirmed}
          data-testid={`shopping-ai-confirm-product-${candidate.product_id}`}
        >
          {confirming === candidate.product_id
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : confirmed
              ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {T("Tracked")}</>
              : T("This is the one")}
        </Button>
        {candidate.url && (
          <Button asChild variant="outline" size="icon" className="rounded-sm h-9 w-9" title={T("Open exact product")}>
            <a href={candidate.url} target="_blank" rel="noopener noreferrer" data-testid={`shopping-ai-open-product-${candidate.product_id}`}>
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

// Human-readable stock + aisle lines from a backend location row.
const stockLine = (loc, storeName, T) => {
  if (loc.availability === "in_stock") {
    const qty = loc.stock_quantity != null ? `${loc.stock_quantity} ` : "";
    return `${qty}${T("in stock")} · ${loc.stock_store || storeName}`;
  }
  if (loc.availability === "out_of_stock") return `${T("Out of stock")} · ${loc.stock_store || storeName}`;
  return T("Inventory not verified");
};

const aisleLine = (loc, T) => {
  if (loc.aisle || loc.bay) {
    const parts = [];
    if (loc.aisle) parts.push(`${T("Aisle")} ${loc.aisle}`);
    if (loc.bay) parts.push(`${T("Bay")} ${loc.bay}`);
    return parts.join(" · ");
  }
  if (loc.estimated_location) return `${T("Likely in")}: ${loc.estimated_location} — ${T("verify the bay tag")}`;
  return T("Aisle/bay could not be verified");
};

export const ShoppingAiButton = ({ client, basePath, T = (value) => value, calendarYear, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [turns, setTurns] = useState([]);
  const [store, setStore] = useState(null);
  const [storeLoaded, setStoreLoaded] = useState(false);
  const [editingStore, setEditingStore] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [findingItem, setFindingItem] = useState(null);
  const [opening, setOpening] = useState(false);
  const [sessionId] = useState(() => {
    const value = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    return `shop_${value.replace(/[^A-Za-z0-9_-]/g, "")}`;
  });
  const yearRef = useRef(calendarYear); yearRef.current = calendarYear;
  useEffect(() => { setTurns([]); setQuestion(""); setAsking(false); setConfirming(null); setFindingItem(null); setOpen(false); }, [calendarYear]);

  const loadStore = useCallback(async () => {
    try {
      const { data } = await client.get(`${basePath}/shopping-list/home-depot-store`);
      setStore(data.store || null);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setStoreLoaded(true);
    }
  }, [client, basePath]);

  useEffect(() => {
    if (!storeLoaded) loadStore();
  }, [storeLoaded, loadStore]);

  const togglePanel = async () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    toast.dismiss();
    setOpening(true);
    if (!storeLoaded) await loadStore();
    setOpen(true);
    setOpening(false);
  };

  const ask = async (overrideText) => {
    const text = (overrideText ?? question).trim();
    if (!text || asking || !store || disabled) return;
    setQuestion("");
    setAsking(true);
    setTurns((previous) => [...previous, { q: text, a: null }]);
    try {
      const { data } = await client.post(`${basePath}/shopping-list/ask`, {
        message: text,
        session_id: sessionId,
        ...(calendarYear ? { calendar_year: calendarYear } : {}),
      });
      if (yearRef.current !== calendarYear) return;
      setTurns((previous) => previous.map((turn, index) => (
        index === previous.length - 1 ? {
          ...turn,
          a: data.answer,
          responseType: data.response_type,
          clarification: data.clarification,
          candidates: data.candidates || [],
          locations: data.locations || [],
          itemId: data.item_id,
        } : turn
      )));
    } catch (error) {
      const message = formatErr(error.response?.data?.detail) || error.message;
      if (yearRef.current !== calendarYear) return;
      setTurns((previous) => previous.map((turn, index) => (
        index === previous.length - 1 ? { ...turn, a: message } : turn
      )));
    } finally {
      if (yearRef.current === calendarYear) setAsking(false);
    }
  };

  const confirmProduct = async (turnIndex, itemId, candidate, { autoContinue = true } = {}) => {
    setConfirming(candidate.product_id);
    try {
      const { data } = await client.put(`${basePath}/shopping-list/${itemId}/home-depot-product`, {
        session_id: sessionId,
        product_id: candidate.product_id,
      });
      if (yearRef.current !== calendarYear) return;
      window.dispatchEvent(new CustomEvent("shopping-product-tracked", { detail: data.item }));
      setTurns((previous) => previous.map((turn, index) => (
        index === turnIndex ? {
          ...turn,
          confirmedProductId: candidate.product_id,
          confirmedByItem: { ...(turn.confirmedByItem || {}), [itemId]: candidate.product_id },
        } : turn
      )));
      toast.success(T("Exact product saved to this shopping item."));
      if (autoContinue) await ask("Continue with the next product.");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setConfirming(null);
    }
  };

  // "Look for the product" — on-demand candidate search for one list item.
  const findProduct = async (turnIndex, itemId) => {
    setFindingItem(itemId);
    try {
      const { data } = await client.post(`${basePath}/shopping-list/${itemId}/find-product`, {
        session_id: sessionId,
      });
      if (yearRef.current !== calendarYear) return;
      setTurns((previous) => previous.map((turn, index) => (
        index === turnIndex ? {
          ...turn,
          itemCandidates: { ...(turn.itemCandidates || {}), [itemId]: data.candidates || [] },
          itemNote: { ...(turn.itemNote || {}), [itemId]: (data.candidates || []).length ? null : data.answer },
        } : turn
      )));
      if (!(data.candidates || []).length) toast.message(data.answer);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setFindingItem(null);
    }
  };

  const { recording, transcribing, toggle } = useVoiceRecorder(client, `${basePath}/voice/transcribe`, (text) => {
    if (text) ask(text);
  });

  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      <button
        type="button"
        onClick={togglePanel}
        disabled={opening || disabled}
        className="fixed bottom-6 right-4 sm:right-6 z-[1000000000] w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        title={T("Ask the shopping assistant")}
        data-testid="shopping-ai-fab"
      >
        {opening ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-[1000000000] w-[calc(100vw-2rem)] sm:w-[380px] max-h-[72vh] flex flex-col border border-border rounded-sm bg-background shadow-2xl" data-testid="shopping-ai-panel">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between shrink-0">
            <div className="text-sm font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> {T("Shopping assistant")}
            </div>
            <button type="button" onClick={() => setOpen(false)} title={T("Close")} data-testid="shopping-ai-close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {!storeLoaded ? (
            <div className="p-6 flex items-center justify-center" data-testid="shopping-ai-store-loading">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : (!store || editingStore) ? (
            <StorePicker
              client={client}
              basePath={basePath}
              currentStore={store}
              onSelected={(selected) => { setStore(selected); setEditingStore(false); }}
              onCancel={() => setEditingStore(false)}
              T={T}
            />
          ) : (
            <>
              <div className="px-3 py-2 border-b border-border bg-secondary/30 flex items-start gap-2 shrink-0" data-testid="shopping-ai-selected-store">
                <MapPin className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold">{store.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate" title={store.address}>{store.address}</div>
                </div>
                <button type="button" onClick={() => setEditingStore(true)} className="text-xs text-primary hover:underline shrink-0" data-testid="shopping-ai-change-store">
                  {T("Change")}
                </button>
              </div>
              <div className="p-2 border-b border-border shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm w-full justify-start"
                  onClick={() => ask("I'm going to Home Depot. What on my shopping list can I buy there?")}
                  disabled={asking}
                  data-testid="shopping-ai-aisle-bay-list"
                >
                  <MapPin className="w-3.5 h-3.5 mr-2" /> {T("Home Depot list & locations")}
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
                {turns.length === 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="shopping-ai-empty-message">
                    {T("Ask what to buy. Your saved store will be used automatically for stock and location.")}
                  </p>
                )}
                {turns.map((turn, index) => (
                  <div key={`${turn.q}-${index}`} className="space-y-1">
                    <div className="font-semibold text-xs text-muted-foreground" data-testid={`shopping-ai-question-${index}`}>{turn.q}</div>
                    <div className="whitespace-pre-wrap" data-testid={`shopping-ai-answer-${index}`}>
                      {turn.a === null ? T("Checking your store…") : turn.a}
                    </div>

                    {turn.responseType === "clarification" && turn.clarification?.quick_replies?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`shopping-ai-clarification-${index}`}>
                        {turn.clarification.quick_replies.map((reply, replyIndex) => (
                          <button
                            type="button"
                            key={reply}
                            onClick={() => ask(reply)}
                            disabled={asking}
                            className="text-xs text-left border border-border rounded-sm px-2 py-1.5 hover:border-primary hover:bg-secondary/40 transition-colors"
                            data-testid={`shopping-ai-clarification-${index}-reply-${replyIndex}`}
                          >
                            {T(reply)}
                          </button>
                        ))}
                      </div>
                    )}

                    {turn.responseType === "product_choices" && turn.candidates?.length > 0 && (
                      <div className="space-y-2 pt-1" data-testid={`shopping-ai-product-choices-${index}`}>
                        {turn.candidates.map((candidate) => (
                          <CandidateCard
                            key={candidate.product_id}
                            candidate={candidate}
                            confirmed={turn.confirmedProductId === candidate.product_id}
                            confirming={confirming}
                            storeName={store.name}
                            T={T}
                            onConfirm={() => confirmProduct(index, turn.itemId, candidate)}
                          />
                        ))}
                      </div>
                    )}

                    {turn.responseType === "answer" && turn.locations?.length > 0 && (
                      <div className="space-y-2 pt-1" data-testid={`shopping-ai-locations-${index}`}>
                        {turn.locations.map((loc) => {
                          const itemCandidates = turn.itemCandidates?.[loc.item_id];
                          const confirmedForItem = turn.confirmedByItem?.[loc.item_id];
                          const noMatchNote = turn.itemNote?.[loc.item_id];
                          return (
                            <div key={loc.item_id} className="border border-border rounded-sm p-2.5" data-testid={`shopping-ai-location-${loc.item_id}`}>
                              <div className="text-xs font-semibold leading-snug">{loc.product}</div>
                              <div className="text-[11px] font-medium mt-0.5">{stockLine(loc, store.name, T)}</div>
                              <div className="text-[11px] text-muted-foreground mt-0.5">{aisleLine(loc, T)}</div>
                              {loc.product_confirmed && loc.match_title && (
                                <div className="text-[11px] text-emerald-700 mt-0.5 flex items-start gap-1">
                                  <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
                                  <span>{T("Matched")}: {loc.match_title}</span>
                                </div>
                              )}
                              <div className="flex gap-2 mt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-sm h-9 flex-1"
                                  onClick={() => findProduct(index, loc.item_id)}
                                  disabled={findingItem === loc.item_id}
                                  data-testid={`shopping-ai-find-product-${loc.item_id}`}
                                >
                                  {findingItem === loc.item_id
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <><Search className="w-4 h-4 mr-1.5" /> {loc.product_confirmed ? T("Change product") : T("Look for the product")}</>}
                                </Button>
                                {loc.url && (
                                  <Button asChild variant="outline" size="icon" className="rounded-sm h-9 w-9" title={T("Open on Home Depot")}>
                                    <a href={loc.url} target="_blank" rel="noopener noreferrer" data-testid={`shopping-ai-location-open-${loc.item_id}`}>
                                      <ExternalLink className="w-4 h-4" />
                                    </a>
                                  </Button>
                                )}
                              </div>
                              {noMatchNote && (
                                <div className="text-[11px] text-muted-foreground mt-2" data-testid={`shopping-ai-location-nomatch-${loc.item_id}`}>{noMatchNote}</div>
                              )}
                              {itemCandidates?.length > 0 && (
                                <div className="space-y-2 mt-2" data-testid={`shopping-ai-location-candidates-${loc.item_id}`}>
                                  {itemCandidates.map((candidate) => (
                                    <CandidateCard
                                      key={candidate.product_id}
                                      candidate={candidate}
                                      confirmed={confirmedForItem === candidate.product_id}
                                      confirming={confirming}
                                      storeName={store.name}
                                      T={T}
                                      onConfirm={() => confirmProduct(index, loc.item_id, candidate, { autoContinue: false })}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-2 border-t border-border flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={toggle}
                  disabled={transcribing}
                  className={`h-9 w-9 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${recording ? "bg-red-500 text-white border-red-500 animate-pulse" : "border-border hover:bg-secondary/50"}`}
                  title={recording ? T("Tap to stop") : T("Ask by voice")}
                  data-testid="shopping-ai-mic"
                >
                  {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                </button>
                <Input
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter") ask(); }}
                  placeholder={recording ? T("Listening…") : T("Ask what you need…")}
                  className="h-9 rounded-sm text-sm"
                  data-testid="shopping-ai-input"
                />
                <Button size="sm" className="rounded-sm h-9" onClick={() => ask()} disabled={asking || !question.trim()} data-testid="shopping-ai-send">
                  {asking ? "…" : T("Ask")}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>,
    document.body,
  );
};
